import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiGithub, FiLinkedin, FiMail, FiExternalLink } from 'react-icons/fi';
import { Typewriter } from 'react-simple-typewriter';
import Magnetic from '../common/Magnetic';

const TICKER_ITEMS = [
  'AI & Machine Learning',
  'Computer Vision',
  'NLP',
  'Full Stack Development',
  'Flutter',
  'Startup CTO',
];

const HeroSection = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const spotlightRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const spotlight = spotlightRef.current;
    if (!section || !spotlight) return;

    const handleMove = (e) => {
      const rect = section.getBoundingClientRect();
      spotlight.style.setProperty('--x', `${e.clientX - rect.left}px`);
      spotlight.style.setProperty('--y', `${e.clientY - rect.top}px`);
    };

    section.addEventListener('mousemove', handleMove);
    return () => section.removeEventListener('mousemove', handleMove);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const lineVariants = {
    hidden: { y: '110%' },
    visible: {
      y: '0%',
      transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
    },
  };

  const socialLinks = [
    { icon: FiGithub, href: 'https://github.com/HARISMUGHAL', label: 'GitHub' },
    { icon: FiLinkedin, href: 'https://www.linkedin.com/in/haris-mughal-183706341/', label: 'LinkedIn' },
    { icon: FiMail, href: 'mailto:harixmughal65@gmail.com', label: 'Email' },
  ];

  return (
    <section
      id="hero"
      ref={sectionRef}
      className="min-h-screen pt-28 pb-10 relative overflow-hidden flex flex-col justify-center"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 z-0">
        {/* Dot grid texture */}
        <div
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black 40%, transparent 100%)',
          }}
        />

        {/* Cursor-follow spotlight (desktop only) */}
        <div
          ref={spotlightRef}
          className="hidden lg:block absolute inset-0 transition-opacity duration-300"
          style={{
            background: 'radial-gradient(500px circle at var(--x, 50%) var(--y, 50%), rgba(108, 92, 231, 0.12), transparent 70%)',
          }}
        />

        {/* Gradient orbs */}
        <motion.div
          animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-20 left-10 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
        />
        <motion.div
          animate={{ x: [0, -100, 0], y: [0, -50, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-10 right-10 w-72 h-72 bg-accent-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
        />
      </div>

      {/* Content */}
      <div className="container-custom relative z-10 w-full">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          {/* Eyebrow badges */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/30 w-fit">
              <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse" />
              <span className="text-xs sm:text-sm text-primary-500 font-semibold">
                AI & ML Engineer • Full Stack & Flutter Developer
              </span>
            </div>
            <a
              href="https://zynqoraedge.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/10 border border-accent-500/30 w-fit hover:bg-accent-500/20 transition-all duration-300"
            >
              <div className="w-2 h-2 bg-accent-500 rounded-full animate-pulse" />
              <span className="text-xs sm:text-sm text-accent-500 font-semibold">CTO @ Zynqora Edge</span>
              <FiExternalLink className="text-xs text-accent-500" />
            </a>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-12 items-end">
            {/* Left - Kinetic typography */}
            <div>
              <h1 className="font-bold text-white leading-[0.95] tracking-tight mb-6 text-[13vw] sm:text-[9vw] lg:text-[5.5vw]">
                <span className="block overflow-hidden">
                  <motion.span variants={lineVariants} className="block">
                    Muhammad
                  </motion.span>
                </span>
                <span className="block overflow-hidden">
                  <motion.span variants={lineVariants} className="block text-gradient">
                    Haris Kamran
                  </motion.span>
                </span>
              </h1>

              {/* Typewriter */}
              <motion.div variants={itemVariants} className="mb-6">
                <div className="text-lg sm:text-xl text-accent-500 font-semibold h-8">
                  <Typewriter
                    words={[
                      'AI & Machine Learning Engineer',
                      'Full Stack Web Developer',
                      'Flutter Mobile Developer',
                      'CTO & Startup Founder',
                    ]}
                    loop
                    cursor
                    cursorStyle="|"
                    typeSpeed={80}
                    deleteSpeed={50}
                    delaySpeed={1500}
                  />
                </div>
              </motion.div>

              {/* Description */}
              <motion.div variants={itemVariants} className="mb-8">
                <p className="text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
                  AI & ML Engineer with hands-on experience in deep learning, computer vision, and NLP. Currently serving as CTO of{' '}
                  <a href="https://zynqoraedge.com" target="_blank" rel="noopener noreferrer" className="text-accent-500 hover:text-primary-500 transition-colors font-medium">
                    Zynqora Edge
                  </a>
                  . Also builds full stack web apps and cross-platform Flutter apps.
                </p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 mb-8">
                <Magnetic>
                  <a
                    href="#projects"
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 text-dark-900 font-bold text-sm hover:shadow-glow-primary transition-shadow duration-300"
                  >
                    View Projects
                    <FiArrowRight className="text-lg" />
                  </a>
                </Magnetic>
                <Magnetic>
                  <a
                    href="#contact"
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg border-2 border-primary-500 text-primary-500 font-bold text-sm hover:bg-primary-500/10 transition-all duration-300"
                  >
                    Collaborate / Hire Me
                  </a>
                </Magnetic>
                <Magnetic>
                  <a
                    href="/Haris_Mughal_CV.pdf"
                    download
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-dark-800 border border-primary-500/20 text-white font-bold text-sm hover:border-primary-500/50 hover:bg-dark-700 transition-all duration-300"
                  >
                    Download CV
                  </a>
                </Magnetic>
              </motion.div>

              {/* Social Links */}
              <motion.div variants={itemVariants} className="flex items-center gap-4">
                <span className="text-sm text-white/50 font-medium">Connect:</span>
                <div className="flex gap-3">
                  {socialLinks.map((social, idx) => {
                    const Icon = social.icon;
                    return (
                      <motion.a
                        key={idx}
                        href={social.href}
                        target={social.href.startsWith('mailto:') ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.2, y: -5 }}
                        whileTap={{ scale: 0.9 }}
                        className="p-3 rounded-lg bg-primary-500/10 text-primary-500 hover:bg-primary-500 hover:text-dark-900 transition-all duration-300"
                        title={social.label}
                      >
                        <Icon className="text-lg" />
                      </motion.a>
                    );
                  })}
                </div>
              </motion.div>
            </div>

            {/* Right - Portrait */}
            <motion.div variants={itemVariants} className="flex justify-center lg:justify-end">
              <div className="relative w-full max-w-xs lg:max-w-sm">
                {/* Glow Background */}
                <motion.div
                  animate={{
                    boxShadow: [
                      '0 0 20px rgba(108, 92, 231, 0.3)',
                      '0 0 40px rgba(108, 92, 231, 0.5)',
                      '0 0 20px rgba(108, 92, 231, 0.3)',
                    ],
                  }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute inset-0 rounded-2xl"
                />

                {/* Image Container with reveal wipe */}
                <div className="relative rounded-2xl overflow-hidden border-2 border-primary-500/30 bg-dark-800">
                  {!imageLoaded && (
                    <div className="absolute inset-0 bg-gradient-to-br from-dark-700 to-dark-800 animate-pulse" />
                  )}
                  <motion.img
                    initial={{ scale: 1.15 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    src="/assets/2.jpg"
                    alt="Muhammad Haris Kamran"
                    onLoad={() => setImageLoaded(true)}
                    className="w-full h-full object-cover"
                  />
                  <motion.div
                    initial={{ y: '0%' }}
                    animate={{ y: '-100%' }}
                    transition={{ duration: 0.9, delay: 0.2, ease: [0.83, 0, 0.17, 1] }}
                    className="absolute inset-0 bg-gradient-to-br from-primary-500 to-accent-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-transparent to-transparent opacity-40" />
                </div>

                {/* Floating Badge */}
                <motion.div
                  animate={{ y: [0, -10, 0], x: [0, 5, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="absolute -bottom-4 -right-4 p-4 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 shadow-lg"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-dark-900 rounded-full animate-pulse" />
                    <div className="text-xs font-bold text-dark-900">Open to Work</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Tech marquee ticker */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.8 }}
        className="relative z-10 mt-12 lg:mt-16 border-y border-white/5 py-4 overflow-hidden"
      >
        <div className="flex whitespace-nowrap animate-marquee">
          {[...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS].map((item, idx) => (
            <span key={idx} className="flex items-center text-sm sm:text-base font-semibold text-white/40 mx-6">
              {item}
              <span className="mx-6 text-accent-500/70">✦</span>
            </span>
          ))}
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="hidden sm:flex absolute bottom-4 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-xs text-white/50 font-medium">Scroll to explore</span>
          <div className="w-6 h-10 rounded-full border-2 border-primary-500/30 flex items-start justify-center pt-2">
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-2 bg-primary-500 rounded-full"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
